import { type FC } from 'react';
import { IconTrash } from '@tabler/icons-react';
import { formatDistanceToNow } from 'date-fns';

import type { OngoingEvaluation } from '@/types';
import { ButtonField } from '@wk/components-react16';
import { Link } from 'react-router';

interface OngoingEvaluationItemProps {
  evaluation: OngoingEvaluation;
  onDelete: (jobId: string) => void;
  onViewResults: () => void;
  /**
   * Router path for "View Results". Defaults to the ORR evaluation route;
   * pass `null` in portals without that route to keep the action as a plain
   * button that only fires `onViewResults`.
   */
  viewResultsTo?: string | null;
}

const progressBarColors = {
  Failed: 'bg-red-500',
  TimedOut: 'bg-amber-500',
  Completed: 'bg-green-500',
  Running: 'bg-blue-500',
  Pending: 'bg-blue-500',
};

const OngoingEvaluationItem: FC<OngoingEvaluationItemProps> = ({
  evaluation,
  onDelete,
  onViewResults,
  viewResultsTo,
}) => {
  const isIndeterminate = ['Running', 'Pending'].includes(evaluation.status);
  const recordId = evaluation.output?.recordId ?? evaluation.evaluationId;
  const resultsTo =
    viewResultsTo === undefined
      ? recordId
        ? `/evaluations/${recordId}`
        : null
      : viewResultsTo;
  const identifierLabel =
    evaluation.source === 'site-compliance' ? 'Site ID' : 'BIT ID';

  return (
    <div className="bg-white border border-gray-200 py-2 px-4 mb-4 relative">
      {/* Delete Icon */}
      <button
        onClick={() => onDelete(evaluation.jobId)}
        className="absolute top-3 right-3 text-gray-400 hover:text-red-500 cursor-pointer transition-colors"
        aria-label="Delete evaluation"
      >
        <IconTrash size={16} />
      </button>

      {/* Service Name */}
      <h3 className="text-sm font-semibold text-gray-900 mb-1">
        {evaluation.applicationName}
      </h3>

      {/* ID */}
      <p className="text-xs text-gray-600 mb-1">
        {identifierLabel}: {evaluation.siteId ?? evaluation.bitId}
      </p>

      {/* Job ID */}
      <p className="text-xs text-gray-600 mb-3 font-medium">
        Job ID: {evaluation.jobId}
      </p>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 h-1 mb-3 overflow-hidden">
        {isIndeterminate ? (
          <div
            className="h-1 bg-[#005b92]"
            style={{
              width: '30%',
              transform: 'translateX(-100%)',
              animation: 'progressBarLoading 1.8s ease-in-out infinite',
            }}
          />
        ) : (
          <div
            className={`h-1 w-full ${progressBarColors[evaluation.status]}`}
          />
        )}
      </div>

      {/* View Results and Timestamp */}
      <div className="flex justify-between items-center">
        {evaluation.status === 'Completed' && recordId && (
          <ButtonField mode="text">
            {resultsTo ? (
              <Link
                onClick={() => {
                  onDelete(evaluation.jobId);
                  onViewResults();
                }}
                to={resultsTo}
                className=""
              >
                View Results
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => {
                  onDelete(evaluation.jobId);
                  onViewResults();
                }}
              >
                View Results
              </button>
            )}
          </ButtonField>
        )}
        {(evaluation.status === 'Failed' || evaluation.status === 'TimedOut') && (
          <div className='text-xs text-red-500'>{evaluation.message}
            <span> </span>
            <a
              href="#"
              className='text-xs text-red-500 underline'
              target="__blank"
              onClick={(e) => {
                e.preventDefault();
                onDelete(evaluation.jobId);
              }}
            >
              <span>clear</span>
            </a>
          </div>
        )}
        <span className="text-xs text-gray-500">
          {formatDistanceToNow(new Date(evaluation.startedAt), {
            addSuffix: true,
          })}
        </span>
      </div>
    </div>
  );
};

export default OngoingEvaluationItem;
